import { forwardRef, useId } from "react";
import "./Input.css";

const Input = forwardRef(({
  label,
  helperText,
  error,
  success,
  iconStart,
  iconEnd,
  fullWidth = true,
  required = false,
  textarea = false,
  className = "",
  ...props
}, ref) => {
  const generatedId = useId();
  const inputId = props.id || generatedId;
  const messageId = `${inputId}-message`;
  const InputTag = textarea ? "textarea" : "input";
  // `error` may be a boolean (state only) or a message to show; as before,
  // any error hides the helper text.
  const hasMessage = error ? error !== true : Boolean(helperText);
  const describedBy =
    [props["aria-describedby"], hasMessage ? messageId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className={`ui-input-container ${fullWidth ? "full-width" : ""} ${className}`}>
      {label && (
        <label className="ui-input-label" htmlFor={inputId}>
          {label}
          {required && <span className="ui-input-required" aria-hidden="true">*</span>}
        </label>
      )}

      <div
        className={`ui-input-wrapper ${error ? "error" : ""} ${success ? "success" : ""} ${
          iconStart ? "has-icon-start" : ""
        } ${iconEnd ? "has-icon-end" : ""}`}
      >
        {iconStart && <span className="ui-input-icon start" aria-hidden="true">{iconStart}</span>}

        {/* `required` stays visual/ARIA only: native validation would start
            blocking forms that currently submit and validate themselves. */}
        <InputTag
          ref={ref}
          id={inputId}
          className="ui-input"
          aria-invalid={error ? true : undefined}
          aria-required={required || undefined}
          {...props}
          aria-describedby={describedBy}
        />

        {iconEnd && <span className="ui-input-icon end" aria-hidden="true">{iconEnd}</span>}
      </div>

      {error ? (
        <span className="ui-input-error" id={hasMessage ? messageId : undefined}>
          {error}
        </span>
      ) : helperText ? (
        <span className="ui-input-helper" id={messageId}>{helperText}</span>
      ) : null}
    </div>
  );
});

export default Input;
